/**
 * TRAVEL CHARM BUILDER — behaviour for sections/travel-charm-builder.liquid (Group 5 test, 2026-09-15).
 * ---------------------------------------------------------------------------------------------------------------------
 * THE CART IS THE STATE. Picks are derived from /cart.js after every write (same model as bundle-builder.js): a
 * charm line anywhere in the cart is a pick, the bracelet line is the holder. Kept locally: the primary finish
 * (sessionStorage), the bracelet's own finish, and each block's search/tab filter.
 *
 * FINISH MODEL — Exchange Life's (Jorrit 2026-09-15, replacing per-row finish dots): step 1 picks the PRIMARY finish;
 * step 2 shows one complete picker per finish, stacked — the primary block first, the other finish's block behind an
 * "Add 18K Gold Charms" card (or the reverse). A row belongs to its block's finish; adding from it adds that variant.
 * A block's trash removes that finish's charms (and hides a secondary block). The bracelet has its own finish dots and
 * follows the primary until she touches them. Mixing is allowed, never forced.
 *
 * THE CATALOG IS A STATIC ASSET (assets/travel-charms.json, built by scripts/build_travel_charms_catalog.py):
 *   { bracelet: {id, handle, title, v:{g:{id,img}, s:{id,img}}}, charms: [{id, handle, title, place, group, v:{...}}] }
 * UNLISTED products are invisible to collection.products, and 643 tiles want one request anyway.
 *
 * Adds go through /cart/add.js (fires the storefront product_added_to_cart pixel event → Meta AddToCart);
 * quantity changes through /cart/update.js per VARIANT (setVariantDelta: the live discount splits lines, keys are unstable). Every write re-reads /cart.js and announces the cart
 * with the theme's `cart:change` event so the header badge stays right. The page NEVER opens the cart drawer
 * (on our five markets it runs the 4-for-£59 presentation): checkout is the bar's button, straight to /checkout.
 */
// CDN size hint that survives a ?v= cache-buster (the catalog keeps ?v= since 2026-09-15).
const sized = (url, w) => `${url}${url.includes('?') ? '&' : '?'}width=${w}`;

(() => {
  // Charm quick view = the bundle page's quick-view sheet (snippets/bundle-quick-view.liquid, rendered on every product
  // page next to the theme's regular #quick-buy-content): gallery, title, ADD TO CART with the price, the spec bullets
  // lifted from the description, a one-sentence footer, no "view details" link. Same dance as bundle-builder.js's
  // BundleQuickViewModal: fetch the product page, lift #bundle-quick-view-template, hand over to Modal.show — never
  // QuickBuyModal.show, which would fetch again and swap in the regular template.
  customElements.whenDefined('quick-buy-modal').then(() => {
    if (customElements.get('tcb-quick-view-modal')) return;
    const QuickBuyModal = customElements.get('quick-buy-modal');
    const ModalProto = Object.getPrototypeOf(QuickBuyModal.prototype);
    class TcbQuickViewModal extends QuickBuyModal {
      async show() {
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
        const html = await (await fetch(`${window.Shopify.routes.root}products/${this.getAttribute('handle')}`)).text();
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const tpl = doc.getElementById('bundle-quick-view-template') || doc.getElementById('quick-buy-content');
        if (!tpl) return undefined;
        const content = tpl.content;
        content.querySelectorAll('noscript').forEach((n) => n.remove());
        this.replaceChildren(content);
        return ModalProto.show.call(this);
      }
    }
    customElements.define('tcb-quick-view-modal', TcbQuickViewModal);
  });

  if (customElements.get('travel-charm-builder')) return;

  const FINISHES = ['s', 'g'];
  const NAME = { g: 'Gold', s: 'Silver' };
  const SWATCH = { g: '#D49A06', s: '#C6C7CB' };
  const other = (f) => (f === 'g' ? 's' : 'g');
  const listOf = (str) => (str || '').split(',').map((x) => x.trim().toLowerCase()).filter(Boolean);

  class TravelCharmBuilder extends HTMLElement {
    connectedCallback() {
      this.root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      this.freeAt = parseInt(this.dataset.freeAt, 10) || 10;
      this.currency = this.dataset.currency || 'USD';
      this.locale = this.dataset.locale || 'en';
      this.price = {
        charm: { g: +this.dataset.charmPriceG || 0, s: +this.dataset.charmPriceS || 0 },
        bracelet: { g: +this.dataset.braceletPriceG || 0, s: +this.dataset.braceletPriceS || 0 },
      };
      this.labels = {
        first: this.dataset.labelFirst, progress: this.dataset.labelProgress, unlocked: this.dataset.labelUnlocked,
        addBracelet: this.dataset.labelAddBracelet, checkout: this.dataset.labelCheckout, next: this.dataset.labelNext,
      };
      this.step = 'charms'; // wizard: 'charms' | 'bracelet' (the separate material step went 2026-09-19: the finish is a switch over the charm list)
      // Default SILVER (Jorrit 2026-09-15): their review feed runs ~70% silver on charms and 55%+ on holders.
      this.finish = (() => { try { return sessionStorage.getItem('tcb_finish') === 'g' ? 'g' : 's'; } catch (e) { return 's'; } })();
      this.braceletFinish = null; // null = follows the primary finish
      this.tiles = new Map(); // `${finish}:${product id}` → { el, product, finish }
      this.byVariant = new Map(); // variant id → { product, finish }
      this.cart = { items: [], item_count: 0, total_price: 0 };
      this.queue = Promise.resolve();
      // A NUMBER FROM THE START (2026-09-19). It used to be undefined until the first cart write, and goCheckout only left for
      // checkout when pending === 0: on a page load with NO write before the tap (a returning visitor with a saved cart, a
      // reload, Back from checkout without the back-forward cache) undefined === 0 was false and Checkout did nothing, no
      // message, since 2026-09-17. Found by Jorrit on the live page; every test before it had made a write first.
      this.pending = 0;
      this.almost = { g: new Set(listOf(this.dataset.almostGold)), s: new Set(listOf(this.dataset.almostSilver)) };
      this.soldOut = new Set(listOf(this.dataset.soldoutPlaces)); // their outOfStock rows: greyed, a grey pill, no add

      this.$ = (sel) => this.querySelector(sel);
      this.$$ = (sel) => Array.from(this.querySelectorAll(sel));
      this.el = {
        bar: this.$('[data-tcb-bar]'), label: this.$('[data-tcb-bar-label]'),
        fill: this.$('[data-tcb-progress-fill]'), barBtn: this.$('[data-tcb-bar-btn]'), barBtnLabel: this.$('[data-tcb-bar-btn-label]'), lock: this.$('[data-tcb-lock]'),
        nextInline: this.$('[data-tcb-next-inline]'), nextInlineLabel: this.$('[data-tcb-next-inline-label]'), prevInline: this.$('[data-tcb-prev-inline]'), summaryInline: this.$('[data-tcb-summary-inline]'), totalInline: this.$('[data-tcb-total-inline]'), listInline: this.$('[data-tcb-list-inline]'),
        summary: this.$('[data-tcb-summary]'), total: this.$('[data-tcb-total]'), list: this.$('[data-tcb-list]'),
        sumModal: this.$('[data-tcb-summary-modal]'), sumLines: this.$('[data-tcb-sum-lines]'), sumEmpty: this.$('[data-tcb-sum-empty]'), sumCount: this.$('[data-tcb-sum-count]'),
        sumCharmsTotal: this.$('[data-tcb-sum-charms-total]'), sumBracelet: this.$('[data-tcb-sum-bracelet]'), sumNoBracelet: this.$('[data-tcb-sum-no-bracelet]'),
        sumBraceletTotal: this.$('[data-tcb-sum-bracelet-total]'), sumTotal: this.$('[data-tcb-sum-total]'), sumList: this.$('[data-tcb-sum-list]'),
        sumSaveRow: this.$('[data-tcb-sum-save-row]'), sumSave: this.$('[data-tcb-sum-save]'), sumCheckout: this.$('[data-tcb-sum-checkout]'),
        error: this.$('[data-tcb-error]'), notice: this.$('[data-tcb-notice]'), review: this.$('[data-tcb-review]'),
        brImg: this.$('[data-tcb-bracelet-img]'), brPrice: this.$('[data-tcb-bracelet-price]'), brFree: this.$('[data-tcb-bracelet-free]'),
        brTile: this.$('[data-tcb-bracelet-tile]'),
        addBlock: this.$('[data-tcb-add-block]'), addBlockTitle: this.$('[data-tcb-addblock-title]'), addBlockSub: this.$('[data-tcb-addblock-sub]'),
      };
      // one picker per finish — its elements and its own filter state
      this.blocks = {};
      this.$$('[data-tcb-block]').forEach((root) => {
        const f = root.dataset.tcbBlock;
        this.blocks[f] = {
          f, root, group: 'all', query: '', onlySelected: false, open: false, rendered: false,
          grid: root.querySelector('[data-tcb-grid]'), scroll: root.querySelector('[data-tcb-scroll]'), scrollThumb: root.querySelector('[data-tcb-scroll-thumb]'), empty: root.querySelector('[data-tcb-empty]'), search: root.querySelector('[data-tcb-search]'),
          count: root.querySelector('[data-tcb-result-count]'), selectedCount: root.querySelector('[data-tcb-selected-count]'),
          price: root.querySelector('[data-tcb-block-price]'), clear: root.querySelector('[data-tcb-block-clear]'),
        };
      });
      this.tpl = this.$('template[data-tcb-tile]');
      const soldEl = this.tpl && this.tpl.content.querySelector('[data-tcb-soldout]');
      this.soldLabel = soldEl ? soldEl.textContent.trim() : ''; // blank label = no sold-out rows
      this.rowTpl = this.$('template[data-tcb-review-row]');
      this.sumTpl = this.$('template[data-tcb-sum-row]');

      this.wire();
      this.paintSwitch();
      fetch(this.dataset.catalog, { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .then((cat) => { this.catalog = cat; this.index(); this.paintBlocks(); this.readCart(); })
        .catch(() => this.fail());

      // Back from checkout can restore this page from the back-forward cache exactly as she left it: busy buttons and a
      // cart that may have changed since. Release the buttons and read the cart again.
      window.addEventListener('pageshow', (e) => { if (!e.persisted) return; this.checkingOut = false; this.paintBusy(false); if (this.catalog) this.readCart(); });
      // ...and release them on the way OUT as well (2026-09-19, Jorrit: Checkout did nothing after a return from checkout in
      // Brave). A page frozen into the back-forward cache keeps its script state, checkingOut included; if the restore does
      // not reach the pageshow handler above for any reason, every later tap was swallowed by the guard in goCheckout.
      window.addEventListener('pagehide', () => { this.checkingOut = false; this.paintBusy(false); });

      // the phone bar is fixed; the page pads itself by the bar's real height (the notice bubble sits above it)
      if (window.ResizeObserver && this.el.bar) new ResizeObserver(() => { document.documentElement.style.setProperty('--tcb-bar-height', `${this.el.bar.offsetHeight}px`); }).observe(this.el.bar);
    }

    /* ---------------------------------------------------------------- setup */
    index() {
      const b = this.catalog.bracelet;
      FINISHES.forEach((f) => { if (b.v[f]) this.byVariant.set(b.v[f].id, { product: b, finish: f }); });
      this.catalog.charms.forEach((c) => {
        FINISHES.forEach((f) => { if (c.v[f]) this.byVariant.set(c.v[f].id, { product: c, finish: f }); });
      });
      // Places hidden from the picker (section setting; their picker omits 30 of the variants they carry — contested and
      // sanctioned places mostly — read 2026-09-16). The products stay; the page just never lists them.
      const hidden = new Set(listOf(this.dataset.hiddenPlaces));
      // search key = the PLACE only (every title ends in "Flag Charm", so "cha" used to match all 643 rows)
      const groups = new Set(listOf(this.dataset.groups)); // enabled charm groups (section setting); empty = all
      this.searchIndex = this.catalog.charms.filter((c) => !hidden.has(c.place.toLowerCase()) && (groups.size === 0 || groups.has(c.group))).map((c) => ({ c, k: c.place.toLowerCase() }));
      // Well-known places first, then the alphabet (Jorrit 2026-09-15): the pinned names come from the section setting in
      // the order given; the pin holds inside every group chip and while searching.
      const pinned = listOf(this.dataset.pinnedPlaces);
      if (pinned.length) {
        const rank = new Map(pinned.map((n, i) => [n, i]));
        const head = this.searchIndex.filter((x) => rank.has(x.c.place.toLowerCase())).sort((a, b) => rank.get(a.c.place.toLowerCase()) - rank.get(b.c.place.toLowerCase()));
        const tail = this.searchIndex.filter((x) => !rank.has(x.c.place.toLowerCase()));
        this.searchIndex = head.concat(tail);
        this.pinnedIds = new Set(head.map((x) => x.c.id));
      }
    }

    wire() {
      this.wireTips();
      this.$$('[data-tcb-finish]').forEach((btn) => btn.addEventListener('click', () => this.setFinish(btn.dataset.tcbFinish)));
      // per-block controls: search, group chips, All / Selected, trash, and the delegated row clicks
      Object.values(this.blocks).forEach((blk) => {
        blk.root.querySelectorAll('[data-tcb-group]').forEach((btn) => btn.addEventListener('click', () => {
          blk.group = btn.dataset.tcbGroup;
          blk.root.querySelectorAll('[data-tcb-group]').forEach((b) => { const on = b === btn; b.classList.toggle('is-active', on); b.setAttribute('aria-selected', on); });
          this.renderGrid(blk.f);
        }));
        let t;
        blk.search.addEventListener('input', () => {
          clearTimeout(t);
          t = setTimeout(() => { blk.query = blk.search.value.trim().toLowerCase(); this.renderGrid(blk.f); }, 120);
        });
        blk.root.querySelectorAll('[data-tcb-only]').forEach((b) => b.addEventListener('click', () => {
          blk.onlySelected = b.dataset.tcbOnly === 'selected';
          blk.root.querySelectorAll('[data-tcb-only]').forEach((x) => x.classList.toggle('is-active', x === b));
          this.renderGrid(blk.f);
        }));
        if (blk.clear) blk.clear.addEventListener('click', () => this.clearFinish(blk.f)); // no bin in the markup since 2026-09-19
        blk.grid.addEventListener('scroll', () => this.paintScroll(blk), { passive: true });
        blk.grid.addEventListener('click', (e) => {
          const tile = e.target.closest('[data-tcb-tile-el]');
          if (!tile) return;
          const entry = this.tiles.get(`${blk.f}:${tile.dataset.productId}`);
          if (!entry || this.isSoldOut(entry.product)) return;
          const btn = e.target.closest('[data-tcb-add],[data-tcb-inc],[data-tcb-dec]');
          if (btn) {
            if (btn.hasAttribute('data-tcb-dec')) this.remove(entry.product, blk.f); else this.add(entry.product, blk.f);
            return;
          }
          // Anywhere else on the row — flag, name, the space between — opens the charm's sheet (Jorrit 2026-09-15).
          if (e.target.closest('.tcb__stepper, .tcb__qty')) return; // the quantity readout is not a tap target either
          this.openQuickView(entry.product, blk.f);
        });
        // keyboard: the row is focusable (tabindex 0); Enter/Space on the row itself opens the sheet
        blk.grid.addEventListener('keydown', (e) => {
          if ((e.key !== 'Enter' && e.key !== ' ') || !e.target.matches('[data-tcb-tile-el]')) return;
          const entry = this.tiles.get(`${blk.f}:${e.target.dataset.productId}`);
          if (entry && !this.isSoldOut(entry.product)) { e.preventDefault(); this.openQuickView(entry.product, blk.f); }
        });
      });
      // the card under the primary block opens the other finish's block
      if (this.el.addBlock) this.el.addBlock.addEventListener('click', () => this.openBlock(other(this.finish)));
      // wizard: Previous, the step-3 back link, and the bar's one button (Next on steps 1–2, checkout on step 3)
      const prev = this.$('[data-tcb-prev]');
      if (prev) prev.addEventListener('click', () => this.setStep('charms'));
      this.$$('[data-tcb-goto]').forEach((t) => t.addEventListener('click', () => this.setStep(t.dataset.tcbGoto)));
      if (this.el.nextInline) this.el.nextInline.addEventListener('click', () => this.el.barBtn.click()); // the panel's Next = the bar's button
      if (this.el.prevInline) this.el.prevInline.addEventListener('click', () => { const prev = this.$('[data-tcb-prev]'); if (prev) prev.click(); }); // the panel's Previous = the bar's
      if (this.el.summaryInline && this.el.summary) this.el.summaryInline.addEventListener('click', () => this.el.summary.click()); // the panel's Summary = the bar's
      // the fixed bar appears only once the panel's own Next has scrolled ABOVE the viewport (phones): never while it is
      // on screen, never while it is still below the fold — one Next in view at a time, and the bar is a "you passed
      // it" affordance, not a duplicate (Jorrit 2026-09-16)
      // TWO IntersectionObservers, no scroll listener (2026-09-17): a per-frame scroll check + requestAnimationFrame forced a
      // main-thread update on EVERY scroll frame (traced on the live page: 377 of 378 frames needed the main thread, against
      // 51 of 370 on a product page), which is what made this page checkerboard on phones. Observers cost nothing while
      // scrolling. A = the button in the real viewport; B = the button in a viewport extended far upward, so B is true
      // when the button is in view OR above it. The bar shows when B && !A, i.e. she has scrolled past the button. B also
      // flips on a jump from far below straight to the top (above -> below), which a single observer never reported.
      if (this.el.nextInline && this.el.bar && window.IntersectionObserver) {
        let inView = false, aboveOrIn = false;
        const apply = () => this.el.bar.classList.toggle('is-offscreen', !(aboveOrIn && !inView));
        new IntersectionObserver((es) => { inView = es[es.length - 1].isIntersecting; apply(); }, { threshold: 0 }).observe(this.el.nextInline);
        new IntersectionObserver((es) => { aboveOrIn = es[es.length - 1].isIntersecting; apply(); }, { threshold: 0, rootMargin: '100000px 0px 0px 0px' }).observe(this.el.nextInline);
        this.el.bar.classList.add('is-offscreen'); // the markup starts it hidden too, so it never shows before this script runs
      } else if (this.el.bar) this.el.bar.classList.remove('is-offscreen'); // no IntersectionObserver: always show the bar
      this.el.barBtn.addEventListener('click', () => {
        // their Next is live at 0 charms but a tap shows a red notice instead of moving on
        if (this.step === 'charms') { if (this.charmCount() > 0) this.setStep('bracelet'); else this.notice(); return; }
        this.goCheckout();
      });
      // their Summary: the running total in the bar opens the sheet (charm lines with FREE tags, bracelet, subtotal, you save)
      if (this.el.summary) this.el.summary.addEventListener('click', () => { this.paintSummary(); if (this.el.sumModal && this.el.sumModal.show) this.el.sumModal.show(); });
      // the header's cart icon on this page (sections/header.liquid, data-tcb-open-summary) opens the same sheet instead of the cart drawer (2026-09-16)
      // delegated: the header section can be re-rendered (theme editor, dev-server hot reload), which dropped a listener bound to the old icon (2026-09-17)
      document.addEventListener('click', (e) => { const a = e.target.closest && e.target.closest('[data-tcb-open-summary]'); if (!a) return; e.preventDefault(); this.paintSummary(); if (this.el.sumModal && this.el.sumModal.show) this.el.sumModal.show(); });
      if (this.el.sumModal) {
        this.el.sumModal.addEventListener('click', (e) => {
          const close = e.target.closest('[data-tcb-sum-close]');
          if (close) { this.el.sumModal.hide(); return; }
          const go = e.target.closest('[data-tcb-sum-goto]');
          if (go) {
            // THE BRACELET BUTTON DOES WHAT IT SAYS (2026-09-19). With no bracelet in the cart it used to be a jump to step 3
            // labelled "Add bracelet": a Clarity session (09-18, the 16-charm abandoned checkout) tapped it and went to Checkout
            // seven seconds later, TWICE, believing it was added. Now the tap claims the bracelet in her finish and she stays in
            // the sheet, where the line appears with its FREE tag. The Charms button is unchanged: picking needs the list.
            if (go.dataset.tcbSumGoto === 'bracelet' && this.catalog && !this.braceletInCart()) { this.setBracelet(this.finishFor(this.catalog.bracelet)); return; }
            // with a bracelet in the cart the same button is the step's one-tap finish switch ("Switch to gold" / "Switch to
            // silver"), also without leaving the sheet; removing it is the line's bin icon
            if (go.dataset.tcbSumGoto === 'bracelet' && this.catalog && this.braceletInCart()) {
              const to = this.braceletInCart() === 'g' ? 's' : 'g';
              if (this.catalog.bracelet.v[to]) { this.braceletFinish = to; this.setBracelet(to); }
              return;
            }
            this.el.sumModal.hide(); this.setStep(go.dataset.tcbSumGoto); return;
          }
          const trash = e.target.closest('[data-tcb-sum-trash]');
          if (trash) {
            const line = this.cart.items.find((it) => it.key === trash.closest('[data-line-key]').dataset.lineKey);
            if (line) this.changeLine(line, 0);
          }
        });
        if (this.el.sumCheckout) this.el.sumCheckout.addEventListener('click', () => this.goCheckout());
      }
      // step-3 review rows: – / + act on the LINE (a row is one variant)
      this.el.review.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-tcb-row-inc],[data-tcb-row-dec]');
        if (!btn) return;
        const row = btn.closest('[data-line-key]');
        const line = this.cart.items.find((it) => it.key === row.dataset.lineKey);
        if (!line) return;
        this.changeLine(line, line.quantity + (btn.hasAttribute('data-tcb-row-inc') ? 1 : -1));
      });
      // bracelet tile: dots + buy controls
      this.el.brTile.addEventListener('click', (e) => {
        if (!this.catalog) return;
        // "Switch to gold" / "Switch to silver": one tap swaps the bracelet to the other finish
        const sw = e.target.closest('[data-tcb-br-switch]');
        if (sw) { const to = this.braceletInCart() === 'g' ? 's' : 'g'; if (this.catalog.bracelet.v[to]) { this.braceletFinish = to; this.setBracelet(to); } return; }
        const dot = e.target.closest('[data-tcb-tile-finish]');
        // one bracelet at most: a card picks the finish, and swaps it when a bracelet is already in the cart
        if (dot) {
          const f = dot.dataset.tcbTileFinish; this.braceletFinish = f;
          if (this.qty(this.catalog.bracelet) > 0 && this.braceletInCart() !== f) this.setBracelet(f); else this.paintBracelet();
          return;
        }
        if (e.target.closest('[data-tcb-remove-bracelet]')) { this.setBracelet(null); return; }
        if (e.target.closest('[data-tcb-add-bracelet]')) this.setBracelet(this.finishFor(this.catalog.bracelet));
      });
    }

    /* ---------------------------------------------------------------- wizard */
    setStep(step) {
      if (!['charms', 'bracelet'].includes(step)) return;
      this.step = step;
      this.dataset.step = step; // CSS hook (the title block shows on the charm step only)
      if (this.el.notice) this.el.notice.hidden = true;
      { const ni = this.$('[data-tcb-notice-inline]'); if (ni) ni.hidden = true; }
      this.closeTip();
      this.$$('[data-tcb-step]').forEach((sec) => { sec.hidden = sec.dataset.tcbStep !== step; });
      // step header: number, ring (1/2 → 2/2 of the circumference 119.4), sentence heading, subline; Previous on step 2
      const STEPS = ['charms', 'bracelet']; const n = STEPS.indexOf(step) + 1;
      const num = this.$('[data-tcb-step-num]'); if (num) num.textContent = String(n);
      const ring = this.$('[data-tcb-ring]'); if (ring) ring.style.strokeDashoffset = String((119.4 * (1 - n / STEPS.length)).toFixed(1));
      this.placeRail();
      const title = this.$('[data-tcb-step-title]'); if (title) title.textContent = this.dataset['stepTitle' + step[0].toUpperCase() + step.slice(1)] || '';
      this.paintStepSub();
      const prev = this.$('[data-tcb-prev]'); if (prev) prev.hidden = step === 'charms';
      if (this.el.prevInline) this.el.prevInline.hidden = step === 'charms'; // the panel's Previous follows the bar's
      requestAnimationFrame(() => Object.values(this.blocks || {}).forEach((b) => this.paintScroll(b))); // the list was filled while hidden: size its scrollbar now that it shows
      if (this.catalog) { this.paintBlocks(); this.paintBar(); if (step === 'bracelet') { this.paintReview(); this.paintBracelet(); } }
      // back on the charm step (Previous, or the sheet's "Edit charms"): focus the search on mouse devices only — on a phone
      // it would pop the keyboard over the list she came back to (2026-09-19; it used to fire on the way in from the material step)
      if (step === 'charms' && window.matchMedia && window.matchMedia('(hover: hover)').matches) setTimeout(() => { try { this.blocks[this.finish].search.focus({ preventScroll: true }); } catch (e) { /* no-op */ } }, 350);
      // Keep her where she is unless the step header has scrolled off the top: then bring it back under the
      // sticky header (never the page top — the gallery above is not the step).
      const tabs = this.$('.tcb__stephead') || this;
      const header = document.querySelector('.header, #shopify-section-header, store-header');
      const headerH = header ? header.getBoundingClientRect().height : 0;
      const r = tabs.getBoundingClientRect();
      // The bracelet step ALWAYS opens with its header under the sticky header: from the page top the claim button sat
      // below the fold behind the gallery (979px down on an 800px phone, 2026-09-19).
      if (r.top < headerH || step === 'bracelet') window.scrollTo({ top: r.top + window.scrollY - headerH - 8, behavior: 'smooth' });
    }

    /* ---------------------------------------------------------------- finish */
    // The bracelet's finish: its own dots, else the primary. (Charm rows take their block's finish.)
    // the finish of the bracelet in the cart ('s' | 'g'), or null
    braceletInCart() {
      const b = this.catalog.bracelet; const line = this.cart.items.find((it) => it.product_id === b.id && it.quantity > 0);
      if (!line) return null;
      return b.v.g && line.variant_id === b.v.g.id ? 'g' : 's';
    }

    // Put exactly one bracelet of finish f in the cart, or none (f = null): every bracelet line goes, then the chosen one is
    // added. One queued write from a fresh read, so a swap never leaves two finishes behind.
    setBracelet(f) {
      const b = this.catalog.bracelet; const v = f && b.v[f];
      if (f && !v) return;
      // optimistic: drop the bracelet lines, add the new one
      this.cart.items = this.cart.items.filter((it) => it.product_id !== b.id);
      if (v) this.cart.items.unshift({ key: `pending-${v.id}-${Date.now()}`, variant_id: v.id, product_id: b.id, quantity: 1, price: this.price.bracelet[f], original_price: this.price.bracelet[f], final_line_price: this.price.bracelet[f], original_line_price: this.price.bracelet[f], product_title: b.title, variant_title: NAME[f], image: v.img });
      this.cart.item_count = this.cart.items.reduce((n, it) => n + it.quantity, 0);
      this.paintPicks();
      return this.write(async () => {
        const cart = await (await fetch(`${this.root}cart.js`, { headers: { Accept: 'application/json' } })).json();
        const updates = {}; cart.items.filter((it) => it.product_id === b.id).forEach((it) => { updates[it.key] = 0; });
        if (Object.keys(updates).length) {
          const r = await fetch(`${this.root}cart/update.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ updates }) });
          if (!r.ok) throw new Error('update failed');
        }
        if (v) {
          const r = await fetch(`${this.root}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id: v.id, quantity: 1 }] }) });
          if (!r.ok) throw new Error('add failed');
        }
      });
    }

    finishFor(product) {
      let f = this.braceletFinish || this.finish;
      if (!product.v[f]) f = product.v.g ? 'g' : 's';
      return f;
    }

    setFinish(f) {
      if (f !== 'g' && f !== 's') return;
      this.finish = f;
      this.braceletFinish = null; // the bracelet follows again
      try { sessionStorage.setItem('tcb_finish', f); } catch (e) { /* private mode */ }
      this.paintSwitch();
      if (this.catalog) { this.paintBlocks(); this.paintBracelet(); }
    }

    /* ---------------------------------------------------------------- blocks (one picker per finish) */
    // Which blocks show: the primary always, the other one once opened by the card or once the cart holds charms in
    // that finish. Order: primary first. The card sits under the primary and names the other finish.
    paintBlocks() {
      if (!this.catalog) return;
      const primary = this.finish, second = other(primary);
      // ONE LIST (2026-09-19, like Exchange Life's current picker): the switch decides which finish's list is on screen, and
      // picks in the other finish stay in the cart, counted on its switch button (paintPicks), in the flag slots and in the
      // Cart sheet. The stacked second list only comes back with the "Add <other finish> charms" card setting.
      const secondVisible = this.dataset.addBlock === 'true' && (this.blocks[second].open || this.charmCountOf(second) > 0);
      this.classList.toggle('is-two-blocks', secondVisible); // row finish discs show only with two lists on screen
      FINISHES.forEach((f) => {
        const blk = this.blocks[f];
        const show = f === primary || (f === second && secondVisible);
        blk.root.hidden = !show;
        if (show && !blk.rendered) this.renderGrid(f);
        if (blk.price) blk.price.innerHTML = this.escape(this.dataset.blockPriceLine || '{price} per {finish} charm')
          .replace('{price}', `<strong>${this.money(this.price.charm[f])}</strong>`).replace('{finish}', NAME[f].toLowerCase());
        if (blk.clear) blk.clear.hidden = f === primary && this.charmCountOf(f) === 0;
      });
      // DOM order: primary block, then the card, then the other block
      // Move a node ONLY when it is out of place: re-inserting an element, even at the spot it already holds, makes the
      // browser reset the scroll position of every scroller inside it — the charm list jumped back to the top after each
      // add, because this runs on every cart read (Jorrit 2026-09-17).
      const step = this.blocks[primary].root.parentNode;
      if (step.firstChild !== this.blocks[primary].root) step.insertBefore(this.blocks[primary].root, step.firstChild);
      if (this.el.addBlock) {
        if (this.blocks[primary].root.nextSibling !== this.el.addBlock) step.insertBefore(this.el.addBlock, this.blocks[primary].root.nextSibling);
        if (step.lastChild !== this.blocks[second].root) step.appendChild(this.blocks[second].root);
        this.el.addBlock.hidden = secondVisible || this.dataset.addBlock !== 'true'; // off by default since 2026-09-19: the finish switch above the list is how she reaches the other finish
        const name = this.dataset['finishName' + second.toUpperCase()] || NAME[second];
        this.el.addBlockTitle.textContent = (this.dataset.addblockTitle || 'Add {finish}').replace('{finish}', name);
        const disc = this.el.addBlock.querySelector('[data-tcb-addblock-disc]'); if (disc) disc.style.setProperty('--swatch-background', SWATCH[second]);
        this.el.addBlockSub.textContent = `${this.money(this.price.charm[second])} per charm`;
        const mat = this.el.addBlock.querySelector('[data-tcb-addblock-material]'); if (mat) mat.textContent = this.dataset['finishSub' + second.toUpperCase()] || '';
      }
    }

    openBlock(f) {
      this.blocks[f].open = true;
      this.paintBlocks();
      const blk = this.blocks[f];
      setTimeout(() => {
        blk.root.scrollIntoView({ block: 'start', behavior: 'smooth' });
        try { blk.search.focus({ preventScroll: true }); } catch (e) { /* no-op */ }
      }, 50);
    }

    // a block's trash: every charm line in that finish goes; a secondary block closes with them
    clearFinish(f) {
      const lines = this.cart.items.filter((it) => { const hit = this.byVariant.get(it.variant_id); return hit && hit.finish === f && it.product_id !== this.catalog.bracelet.id; });
      if (f !== this.finish) this.blocks[f].open = false;
      if (!lines.length) { this.paintBlocks(); return; }
      return this.write(() => this.setVariantDelta(lines.map((it) => it.variant_id), null)); // every split line of those variants
    }

    /* ---------------------------------------------------------------- charm quick view (the theme's quick-buy modal) */
    openQuickView(product, f) {
      // The theme moves an open modal onto document.body, so keep our own reference from the first call.
      this.qb = this.qb || this.$('[data-tcb-qb]') || document.querySelector('[data-tcb-qb]');
      const modal = this.qb;
      if (!modal || typeof modal.show !== 'function') return;
      const v = product.v[f] || product.v.s || product.v.g;
      modal.setAttribute('handle', `${product.handle}${v ? `?variant=${v.id}` : ''}`);
      if (!modal.dataset.tcbWired) {
        modal.dataset.tcbWired = '1';
        // Capture phase: runs before the theme's <product-form> submit handler (bundle-builder.js does the same).
        modal.addEventListener('submit', (e) => this.onQuickViewSubmit(e), true);
        // The sheet's button says what the row's button says ("Add charm", Jorrit 2026-09-15). The server markup is
        // "<text>Add to cart<span.buy-buttons__price>$12</span>", and the theme re-renders the sheet on every swatch
        // change, so only the bare text node is swapped and a MutationObserver re-applies it after each re-render.
        const label = this.dataset.labelAdd || '';
        const relabel = () => {
          const btn = modal.querySelector('buy-buttons button[type="submit"]'); if (!btn || !label) return;
          const t = Array.from(btn.childNodes).find((n) => n.nodeType === 3 && n.textContent.trim());
          if (t && t.textContent.trim() !== label) t.textContent = label;
        };
        new MutationObserver(relabel).observe(modal, { childList: true, subtree: true });
      }
      modal.show();
    }
    onQuickViewSubmit(e) {
      e.preventDefault(); e.stopImmediatePropagation();
      const id = Number(new FormData(e.target).get('id'));
      const hit = this.byVariant.get(id);
      if (hit && hit.product.id !== this.catalog.bracelet.id) this.add(hit.product, hit.finish); // the sheet's swatch decides the finish; its block opens on the re-read
      if (this.qb && typeof this.qb.hide === 'function') this.qb.hide();
    }

    /* ---------------------------------------------------------------- gallery + headers */
    // The theme's product gallery (rendered for the bracelet) follows the bracelet's finish: dispatch the PDP's own
    // `variant:change` on the hidden form the gallery listens to, with the finish's featured media (id:position from
    // the section's data attributes). The gallery selects that cell exactly as it does for a swatch on a PDP.
    syncGallery(f) {
      const form = document.forms['tcb-gallery-form'];
      const spec = this.dataset['galleryMedia' + f.toUpperCase()];
      if (!form || !spec || this.galleryFinish === f) return;
      const media = (x) => { const [id, position] = (this.dataset['galleryMedia' + x.toUpperCase()] || '').split(':'); return { id: Number(id), position: Number(position) }; };
      const prev = this.galleryFinish; this.galleryFinish = f;
      const other = f === 's' ? 'g' : 's';
      // Only the selected finish's bound render is shown (Jorrit 2026-09-16): the other finish's slide is filtered out
      // of the carousel and the thumbnail strip with the theme's own per-variant filter, so both renders sit at the
      // front of the gallery and the visible bracelet is always slide 1. The filtered-indexes attribute keeps the
      // gallery's position math in step with the hidden cell.
      const gallery = form.closest('travel-charm-builder')?.querySelector('product-gallery') || document.querySelector('product-gallery');
      const carousel = gallery && gallery.querySelector('.product-gallery__carousel');
      const hide = media(other).position ? [media(other).position] : [];
      if (gallery) gallery.setAttribute('filtered-indexes', JSON.stringify(hide));
      if (carousel && typeof carousel.filter === 'function') carousel.filter(hide.map((p) => p - 1));
      // select the finish's own render; on the first call pass the other finish as "previous" so the theme moves off its
      // default (the FIRST variant's media, gold) even when nothing changed
      form.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant: { featured_media: media(f) }, previousVariant: { featured_media: media(prev || other) } } }));
    }

    // ONE progress rail, two places: above the search box on the charm step, under the step on the bracelet step
    placeRail() {
      const rail = this.$('[data-tcb-rail]'); if (!rail) return;
      const to = this.$(this.step === 'charms' ? '[data-tcb-rail-slot]' : '[data-tcb-rail-home]');
      if (to && rail.parentNode !== to) to.appendChild(rail);
    }

    // the step subline ({price}/{finish} fill in from the primary finish)
    paintStepSub() {
      const sub = this.$('[data-tcb-step-sub]'); if (!sub) return;
      sub.classList.remove('is-free');
      if (this.step === 'bracelet' && this.catalog) {
        // their "Add N more charm(s) to get this FREE", counted from the cart; green once she is there
        const n = this.charmCount(); const left = Math.max(this.freeAt - n, 0);
        let line = left > 0 ? (this.dataset.stepSubBracelet || '') : (this.dataset.stepSubBraceletUnlocked || '');
        if (left === 0 && this.braceletInCart()) line = this.dataset.stepSubBraceletClaimed || line; // "unlocked" is stale once she has claimed it (2026-09-19)
        if (left === 1) line = line.replace(/\{left\} more charms\b/, '{left} more charm');
        sub.textContent = line.replace('{left}', left).replace('{n}', n).replace('{value}', this.money(this.price.bracelet[this.finishFor(this.catalog.bracelet)]));
        sub.classList.toggle('is-free', left === 0);
        return;
      }
      const tpl = this.dataset['stepSub' + this.step[0].toUpperCase() + this.step.slice(1)] || '';
      // {material} = the selected finish's material line (the old material cards' sublines): it follows the switch above the list
      sub.textContent = tpl.replace('{material}', this.dataset['finishSub' + this.finish.toUpperCase()] || '').replace('{price}', this.money(this.price.charm[this.finish])).replace('{finish}', NAME[this.finish].toLowerCase()); // sentence case mid-line
    }
    paintSwitch() {
      this.paintStepSub();
      this.$$('[data-tcb-finish]').forEach((b) => { const on = b.dataset.tcbFinish === this.finish; b.classList.toggle('is-active', on); b.setAttribute('aria-checked', on); });
      this.$$('[data-tcb-charm-price]').forEach((s) => { s.textContent = this.money(this.price.charm[s.dataset.tcbCharmPrice]) + ' per charm'; });
      const ml = this.$('[data-tcb-material-line]'); if (ml) ml.textContent = (ml.dataset.tpl || '').replace('{material}', this.dataset['finishSub' + this.finish.toUpperCase()] || ''); // follows the switch
      this.$$('[data-tcb-charm-price-short]').forEach((s) => { s.textContent = this.money(this.price.charm[s.dataset.tcbCharmPriceShort]); }); // the switch: "Silver • $12"
    }

    paintDots(el, product, f) {
      el.querySelectorAll('[data-tcb-tile-finish]').forEach((d) => {
        const has = !!product.v[d.dataset.tcbTileFinish];
        d.hidden = !has;
        d.classList.toggle('is-selected', d.dataset.tcbTileFinish === f); // the theme's .color-swatch selected state
        d.setAttribute('aria-pressed', d.dataset.tcbTileFinish === f);
      });
      const name = el.querySelector('[data-tcb-finish-name]');
      if (name) name.textContent = NAME[f];
    }

    /* ---------------------------------------------------------------- grid (per block) */
    matches(f) {
      const blk = this.blocks[f];
      let list = this.searchIndex.filter((x) => !!x.c.v[f]); // four designs exist in one finish only
      if (blk.group !== 'all') list = list.filter((x) => x.c.group === blk.group);
      if (blk.query) list = list.filter((x) => x.k.includes(blk.query));
      if (blk.onlySelected) list = list.filter((x) => this.qtyOf(x.c, f) > 0);
      return list.map((x) => x.c);
    }

    renderGrid(f) {
      const blk = this.blocks[f];
      const all = this.matches(f);
      const frag = document.createDocumentFragment();
      all.forEach((c) => frag.appendChild(this.tile(c, f)));
      blk.grid.replaceChildren(frag);
      this.paintScroll(blk);
      blk.rendered = true;
      // a divider under the last pinned row (only when an unpinned row follows it)
      if (this.pinnedIds) {
        const last = [...all].reverse().find((c) => this.pinnedIds.has(c.id));
        const lastIdx = last ? all.indexOf(last) : -1;
        all.forEach((c, i) => { const t = this.tiles.get(`${f}:${c.id}`); if (t) t.el.classList.toggle('is-pinned-last', i === lastIdx && i < all.length - 1); });
      }
      blk.empty.hidden = all.length > 0;
      this.paintCount(f);
      this.paintPicks();
    }

    tile(c, f) {
      const key = `${f}:${c.id}`;
      let t = this.tiles.get(key);
      if (t) return t.el;
      const el = this.tpl.content.firstElementChild.cloneNode(true);
      el.dataset.productId = c.id;
      el.querySelector('[data-tcb-place]').textContent = c.place;
      el.querySelector('img').alt = c.title;
      const disc = el.querySelector('[data-tcb-disc]'); if (disc) disc.style.setProperty('--swatch-background', SWATCH[f]);
      t = { el, product: c, finish: f };
      this.tiles.set(key, t);
      this.paintTile(t);
      return el;
    }

    paintTile(t) {
      const f = t.finish;
      const v = t.product.v[f];
      const img = t.el.querySelector('img');
      const src = v && v.img ? sized(v.img, 400) : '';
      if (img.getAttribute('src') !== src) img.src = src;
      this.paintBuy(t.el, this.qtyOf(t.product, f));
      // their per-finish "Almost sold out" pill: the place is in the finish's list (section settings)
      const sold = this.isSoldOut(t.product);
      t.el.classList.toggle('is-soldout', sold);
      const pill = t.el.querySelector('[data-tcb-pill]');
      if (pill) pill.hidden = sold || !(pill.textContent.trim() && this.almost[f].has(t.product.place.toLowerCase()));
      const soldPill = t.el.querySelector('[data-tcb-soldout]');
      if (soldPill) soldPill.hidden = !sold;
    }
    isSoldOut(product) { return !!this.soldLabel && this.soldOut.has(product.place.toLowerCase()); }

    /* ---------------------------------------------------------------- cart */
    // The theme's cart drawer re-renders from section HTML carried on `cart:change` (event.detail.cart.sections[id]),
    // so after every write the cart is re-read through /cart/update.js WITH the drawer's section id (the theme's
    // `cart:prepare-bundled-sections` hook hands it over) — one request returns the cart JSON and that HTML. The
    // initial read is a plain GET and announces nothing: the page rendered with the cart it has.
    bundledSections() {
      const sections = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
      return sections;
    }

    async readCart(announce) {
      // A read is only applied if no tap happened while it was in flight: a tap paints optimistically at once, and a read
      // that STARTED before the tap comes back without that charm, so applying it made the new flag vanish until the
      // tap's own read landed (the flicker, 2026-09-17). The tap's write queues a fresh read, so nothing is lost.
      const stamp = this.writeStamp || 0;
      const fresh = () => stamp === (this.writeStamp || 0);
      try {
        if (announce) {
          const sections = this.bundledSections();
          const res = await fetch(`${this.root}cart/update.js`, {
            method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ sections: sections.join(',') }),
          });
          const json = await res.json();
          if (!fresh()) return;
          this.setCart(json);
        } else {
          const res = await fetch(`${this.root}cart.js`, { headers: { Accept: 'application/json' } });
          const json = await res.json();
          if (!fresh()) return;
          this.setCart(json);
        }
      } catch (e) { this.fail(); }
      this.paintBlocks(); // a charm added in the other finish (the sheet's swatch) opens its block
      this.paintPicks();
      if (announce) this.announce();
    }

    // ONE ROW PER VARIANT (2026-09-17). Since the Travel Charms discount went live, Shopify SPLITS a line whose units
    // are not all discounted alike: two of the same charm come back as two lines, one paid and one free, and the free
    // line's key changes on every recalculation. The page thinks in variants, so every read folds a variant's lines
    // back into one item (quantities and line prices summed, all keys kept in `keys`). The untouched cart stays in
    // cartRaw for the theme's cart:change listeners. Writes never trust a stored key: see setVariantDelta.
    setCart(json) {
      this.cartRaw = json;
      if (!json || !Array.isArray(json.items)) { this.cart = json; return; }
      const seen = new Map(); const items = [];
      json.items.forEach((it) => {
        const plain = !it.properties || Object.keys(it.properties).length === 0;
        const hit = plain && seen.get(it.variant_id);
        if (!hit) { const copy = Object.assign({}, it, { keys: [it.key] }); if (plain) seen.set(it.variant_id, copy); items.push(copy); return; }
        hit.keys.push(it.key);
        hit.quantity += it.quantity;
        ['line_price', 'original_line_price', 'final_line_price', 'total_discount'].forEach((k) => { if (typeof it[k] === 'number') hit[k] = (hit[k] || 0) + it[k]; });
        hit.line_level_discount_allocations = (hit.line_level_discount_allocations || []).concat(it.line_level_discount_allocations || []);
      });
      this.cart = Object.assign({}, json, { items });
    }

    // Change a variant's TOTAL by delta, from a fresh read inside the queued write: the first of its lines takes the new
    // total and every other split line goes to zero, in one update.js call. Verified on the live cart 2026-09-17:
    // change.js by key turned "set to 3" into 4 units and threw 400 on a stale key; update.js keyed by VARIANT id only
    // touches the first split line; this form landed exactly on every step of 2 -> 3 -> 5 -> 1 -> 0.
    async setVariantDelta(variantIds, delta) {
      const ids = new Set([].concat(variantIds));
      const res0 = await fetch(`${this.root}cart.js`, { headers: { Accept: 'application/json' } });
      const cart = await res0.json();
      const updates = {};
      ids.forEach((vid) => {
        const lines = cart.items.filter((it) => it.variant_id === vid);
        if (!lines.length) return;
        const total = lines.reduce((n, it) => n + it.quantity, 0);
        const target = delta === null ? 0 : Math.max(0, total + delta);
        lines.forEach((it, i) => { updates[it.key] = i === 0 ? target : 0; });
      });
      if (!Object.keys(updates).length) return;
      const res = await fetch(`${this.root}cart/update.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates }),
      });
      if (!res.ok) throw new Error('update failed');
    }

    announce() {
      const cart = this.cartRaw || this.cart;
      if (cart && cart.sections && Object.keys(cart.sections).length) {
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { cart, source: 'travel-charm-builder' } }));
      } else {
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      }
    }

    lines(product) { return this.cart.items.filter((it) => it.product_id === product.id); }
    qty(product) { return this.lines(product).reduce((n, it) => n + it.quantity, 0); }
    qtyOf(product, f) { const v = product.v[f]; return v ? this.cart.items.filter((it) => it.variant_id === v.id).reduce((n, it) => n + it.quantity, 0) : 0; }
    charmCount() { return this.cart.items.reduce((n, it) => (this.byVariant.has(it.variant_id) && it.product_id !== this.catalog.bracelet.id ? n + it.quantity : n), 0); }
    charmCountOf(f) { return this.cart.items.reduce((n, it) => { const hit = this.byVariant.get(it.variant_id); return hit && hit.finish === f && it.product_id !== this.catalog.bracelet.id ? n + it.quantity : n; }, 0); }

    write(fn) {
      this.el.error.hidden = true;
      if (this.el.notice) this.el.notice.hidden = true;
      { const ni = this.$('[data-tcb-notice-inline]'); if (ni) ni.hidden = true; }
      // Only the LAST queued write re-reads the cart (2026-09-17). Every write used to re-read, so with two quick taps the
      // first tap's read landed AFTER the second tap's optimistic paint and replaced it with a cart that did not hold the
      // second charm yet: the flag showed, vanished, then came back with the second read. While writes are pending the
      // optimistic copy is the truth on screen; the read after the last one reconciles it.
      this.pending = (this.pending || 0) + 1;
      this.writeStamp = (this.writeStamp || 0) + 1; // a read that started before this tap must not land on top of it (see readCart)
      this.queue = this.queue.then(fn).catch(() => { this.writeFailed = true; this.fail(); }).then(() => {
        this.pending -= 1;
        if (this.pending === 0) return this.readCart(true);
      });
      return this.queue;
    }

    // EVERY CHECKOUT BUTTON GOES THROUGH HERE (2026-09-17). The buttons used to leave on the OPTIMISTIC item count, so a tap
    // while adds were still queued dropped them on navigation: with the tenth charm pending, the page said 10 charms and a
    // free bracelet and checkout opened with nine and the bracelet charged (reproduced live). Same honesty guard as the
    // offer page's bar (bundle-builder.js): wait for every queued write, read the real cart, only then leave. A failed
    // write keeps her here with the reconciled cart and the error line. On a settled cart the wait is one cart read.
    async goCheckout() {
      // the double-tap guard EXPIRES: a checkout that has not left the page within 8 s is not "in progress" any more
      if ((this.checkingOut && Date.now() - (this.checkingOutAt || 0) < 8000) || !this.cart || this.cart.item_count === 0) return;
      this.checkingOut = true; this.checkingOutAt = Date.now();
      this.writeFailed = false;
      this.paintBusy(true);
      try {
        // up to three rounds: a write that landed DURING the cart read used to end the tap silently (no navigation, no
        // message); now the tap waits for it and reads again
        for (let round = 0; round < 3; round++) {
          while (this.pending > 0) await this.queue; // taps made while waiting extend the queue
          if (this.writeFailed) throw new Error('write failed'); // the queue's own re-read already put the real cart on screen
          const res = await fetch(`${this.root}cart.js`, { headers: { Accept: 'application/json' } });
          if (!res.ok) throw new Error('read failed');
          this.setCart(await res.json());
          this.paintPicks();
          if (!this.pending) break;
        }
        if (this.cart.item_count > 0) { window.location.href = `${this.root}checkout`; return; } // stays busy while the page unloads
      } catch (e) { this.fail(); }
      this.checkingOut = false;
      this.paintBusy(false);
    }
    paintBusy(on) {
      [this.el.barBtn, this.el.nextInline, this.el.sumCheckout].forEach((b) => { if (!b) return; if (on) b.setAttribute('aria-busy', 'true'); else b.removeAttribute('aria-busy'); });
    }

    // OPTIMISTIC PAINT (Jorrit 2026-09-16: "the add charm button responds very slowly" — measured 1,060 ms from tap to
    // change, all of it the two round trips): the page's own copy of the cart is updated and repainted the moment she
    // taps, then replaced by the real cart when the write's re-read lands. A pending line carries a synthetic key.
    optimistic(variantId, productId, delta, priceCents) {
      if (!this.cart || !this.cart.items) return;
      const line = this.cart.items.find((it) => it.variant_id === variantId);
      if (line) { line.quantity = Math.max(0, line.quantity + delta); if (line.quantity === 0) this.cart.items = this.cart.items.filter((it) => it !== line); }
      else if (delta > 0) this.cart.items.unshift({ key: `pending-${variantId}-${Date.now()}`, variant_id: variantId, product_id: productId, quantity: delta, price: priceCents, original_price: priceCents, final_line_price: priceCents * delta });
      this.cart.item_count = this.cart.items.reduce((n, it) => n + it.quantity, 0);
      this.paintPicks();
    }

    add(product, f) {
      const v = product.v[f];
      if (!v) return;
      this.optimistic(v.id, product.id, 1, product.id === this.catalog.bracelet.id ? this.price.bracelet[f] : this.price.charm[f]);
      return this.write(async () => {
        const res = await fetch(`${this.root}cart/add.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: v.id, quantity: 1 }] }),
        });
        if (!res.ok) throw new Error('add failed');
      });
    }

    changeLine(line, quantity) {
      const delta = Math.max(quantity, 0) - line.quantity;
      this.optimistic(line.variant_id, line.product_id, delta, line.original_price != null ? line.original_price : line.price);
      return this.write(() => this.setVariantDelta(line.variant_id, quantity <= 0 ? null : delta));
    }

    remove(product, f) {
      const v = product.v[f];
      const line = v && this.cart.items.find((it) => it.variant_id === v.id);
      if (!line) return;
      this.optimistic(v.id, product.id, -1, line.original_price != null ? line.original_price : line.price);
      return this.write(() => this.setVariantDelta(v.id, -1));
    }

    /* ---------------------------------------------------------------- paint */
    paintPicks() {
      if (!this.catalog) return;
      const badges = this.unitBadges();
      this.tiles.forEach((t) => { const q = this.qtyOf(t.product, t.finish); this.paintBuy(t.el, q); const v = t.product.v[t.finish]; this.paintRowBadge(t.el, v ? v.id : null, q, badges); });
      FINISHES.forEach((f) => {
        const blk = this.blocks[f];
        const picked = this.catalog.charms.reduce((n, c) => n + (this.qtyOf(c, f) > 0 ? 1 : 0), 0);
        if (blk.selectedCount) blk.selectedCount.textContent = picked ? `(${picked})` : '';
        const badge = this.$(`[data-tcb-finish-count="${f}"]`); // the switch carries each finish's charm count, so picks in the hidden finish stay visible
        if (badge) { const n = this.charmCountOf(f); badge.textContent = n ? String(n) : ''; badge.hidden = !n; }
        if (blk.clear) blk.clear.hidden = f === this.finish && this.charmCountOf(f) === 0;
      });
      this.paintBracelet();
      this.paintBar();
      if (this.step === 'bracelet') this.paintReview();
    }

    // Pick-order badges: charm units in the order they were added (cart.js lists newest first, so reversed), numbered
    // 1..N; FREE follows the pair rule of pricing() — units sorted by price (stable, so pick order breaks ties), the
    // second of each pair free. One price → every even pick is FREE; mixed finishes → the cheaper of each pair.
    unitBadges() {
      const bid = this.catalog.bracelet.id; const units = [];
      [...this.cart.items].reverse().forEach((it) => {
        if (!this.byVariant.has(it.variant_id) || it.product_id === bid) return;
        for (let i = 0; i < it.quantity; i++) units.push({ variant: it.variant_id, price: it.original_price != null ? it.original_price : it.price, ordinal: units.length + 1, free: false });
      });
      [...units].sort((a, b) => b.price - a.price).forEach((u, i) => { if (i % 2 === 1) u.free = true; });
      const by = new Map();
      units.forEach((u) => { const e = by.get(u.variant) || { ordinals: [], free: 0 }; e.ordinals.push(u.ordinal); if (u.free) e.free += 1; by.set(u.variant, e); });
      return by;
    }
    paintRowBadge(el, variantId, qty, badges) {
      const o = el.querySelector('[data-tcb-order]'); const f = el.querySelector('[data-tcb-row-free]');
      if (!o || !f) return;
      const e = qty > 0 ? badges.get(variantId) : null;
      if (!e) { o.hidden = true; f.hidden = true; return; }
      const ords = e.ordinals; o.textContent = ords.length > 1 ? `${ords[0]}–${ords[ords.length - 1]}` : String(ords[0]); o.hidden = false;
      f.dataset.base = f.dataset.base || f.textContent.trim(); // the tag's own text from the section setting ("FREE")
      f.textContent = e.free === 0 ? f.dataset.base : (ords.length > 1 && e.free < ords.length ? `${e.free} ${f.dataset.base}` : f.dataset.base); f.hidden = e.free === 0;
    }

    // the list's own scrollbar: thumb height = visible share of the rows, position = scroll share; hidden when it all fits
    paintScroll(blk) {
      if (!blk || !blk.scroll || !blk.grid) return;
      const g = blk.grid; const total = g.scrollHeight; const view = g.clientHeight;
      if (total <= view + 1) { blk.scroll.hidden = true; return; }
      blk.scroll.hidden = false;
      const track = blk.scroll.clientHeight; const h = Math.max(28, Math.round(track * view / total));
      const top = (track - h) * (g.scrollTop / (total - view)); // sub-pixel, moved on the compositor: a rounded `top` stepped
      blk.scrollThumb.style.height = h + 'px'; blk.scrollThumb.style.transform = `translate3d(0, ${top.toFixed(2)}px, 0)`;
    }

    paintBuy(el, qty) {
      const add = el.querySelector('[data-tcb-add],[data-tcb-add-bracelet]');
      const stepper = el.querySelector('.tcb__stepper');
      add.hidden = qty > 0;
      stepper.hidden = qty === 0;
      el.querySelector('[data-tcb-qty]').textContent = qty;
      el.classList.toggle('is-in', qty > 0);
    }

    paintBracelet() {
      const b = this.catalog.bracelet;
      const inCart = this.braceletInCart();
      if (inCart) this.braceletFinish = inCart; // the cards always show what is in the cart
      const f = this.finishFor(b);
      const v = b.v[f];
      if (this.el.brImg && v && v.img && this.el.brImg.getAttribute('src') !== sized(v.img, 600)) this.el.brImg.src = sized(v.img, 600); // no tile image since 2026-09-19 (the gallery above shows the finish)
      this.syncGallery(f);
      this.paintDots(this.el.brTile, b, f);
      const free = this.charmCount() >= this.freeAt;
      if (this.el.brPrice) { this.el.brPrice.textContent = this.money(this.price.bracelet[f]); this.el.brPrice.style.textDecoration = free ? 'line-through' : ''; }
      if (this.el.brFree) this.el.brFree.hidden = !free;
      // the two finish cards: each its own price, struck with a FREE tag once the bracelet is free
      this.el.brTile.querySelectorAll('[data-tcb-bracelet-card-price]').forEach((s) => {
        s.textContent = this.money(this.price.bracelet[s.dataset.tcbBraceletCardPrice]); s.style.textDecoration = free ? 'line-through' : '';
        const tag = s.parentNode.querySelector('[data-tcb-bracelet-card-free]'); if (tag) tag.hidden = !free;
      });
      this.el.brTile.querySelectorAll('[data-tcb-tile-finish]').forEach((c) => c.setAttribute('aria-checked', c.dataset.tcbTileFinish === f));
      // no stepper here: Add bracelet, or "Bracelet added · Remove"
      const has = !!inCart;
      const addBtn = this.el.brTile.querySelector('[data-tcb-add-bracelet]');
      if (addBtn) {
        addBtn.hidden = has;
        // the claim button names what the tap gets her: the FREE bracelet at the free count, else the bracelet and its price
        const t = addBtn.querySelector('[data-tcb-add-bracelet-text]');
        if (t) t.textContent = free ? (addBtn.dataset.labelFree || addBtn.dataset.label) : addBtn.dataset.label;
        const pr = addBtn.querySelector('[data-tcb-add-bracelet-price]'); // the price rides behind a drawn dot (CSS), only while the bracelet costs money
        if (pr) { pr.hidden = free; pr.textContent = this.money(this.price.bracelet[f]); }
      }
      // the finish cards are the SWAP control of a bracelet that is in the cart — hidden before, where a pre-selected
      // card read as "already chosen, it comes with my order" (2026-09-19)

      const added = this.el.brTile.querySelector('[data-tcb-bracelet-added]');
      if (added) {
        added.hidden = !has;
        // "Silver Bracelet added" / "18K Gold Bracelet added": the pill names the finish that is in the cart
        const t = added.querySelector('[data-tcb-bracelet-added-text]');
        if (t) t.textContent = (added.dataset.tpl || '{name} added').replace('{name}', (f === 'g' ? added.dataset.nameG : added.dataset.nameS) || '');
        const swl = added.querySelector('[data-tcb-br-switch]'); // names the OTHER finish; hidden when the bracelet only comes in one
        if (swl) { const to = f === 'g' ? 's' : 'g'; swl.hidden = !this.catalog.bracelet.v[to]; swl.textContent = (to === 'g' ? swl.dataset.labelG : swl.dataset.labelS) || ''; }
      }
      this.el.brTile.classList.toggle('is-in', has);
    }

    // a block's list-head left slot: the picked count lives in its Selected pill ("Selected (2)"), so this slot is
    // search feedback only — "4 places" while a query is active, blank otherwise (Jorrit 2026-09-15)
    paintCount(f) {
      const blk = this.blocks[f];
      if (!blk || !blk.count || !this.catalog) return;
      if (!blk.query) { blk.count.textContent = ''; return; }
      const n = this.matches(f).length;
      blk.count.textContent = `${n} ${n === 1 ? 'place' : 'places'}`;
    }

    paintBar() {
      const n = this.charmCount();
      const hasBracelet = this.qty(this.catalog.bracelet) > 0;
      const left = Math.max(this.freeAt - n, 0);
      let label;
      if (n === 0) label = this.labels.first;
      else if (left > 0) label = this.labels.progress;
      else label = hasBracelet ? this.labels.unlocked : this.labels.addBracelet;
      // '{n} charms' reads '1 charm' at one (the settings hold the plural form)
      let text = this.escape(label);
      if (this.step === 'bracelet') text = text.replace(/in the next step/i, 'above'); // on the bracelet step the claim button sits ABOVE both bars ("below" pointed at nothing, 2026-09-19)
      if (n === 1) text = text.replace(/\{n\} charms\b/, '{n} charm');
      const value = this.money(this.price.bracelet[this.finishFor(this.catalog.bracelet)]); // their "(€53,58 Value)" anchor
      this.el.label.innerHTML = text.replace('{n}', `<strong>${n}</strong>`).replace('{left}', `<strong>${left}</strong>`).replace('{value}', value);
      if (this.step === 'bracelet') this.paintStepSub(); // the countdown to FREE follows the cart
      if (this.el.fill) this.el.fill.style.width = `${Math.min(n / this.freeAt, 1) * 100}%`;
      // numbered slots: the first n are on and show the picked charm's flag; one that just came on pops once.
      const bid = this.catalog.bracelet.id; const picks = [];
      [...this.cart.items].reverse().forEach((it) => {
        if (!this.byVariant.has(it.variant_id) || it.product_id === bid) return;
        for (let q = 0; q < it.quantity; q++) picks.push({ variant: it.variant_id, price: it.original_price != null ? it.original_price : it.price, free: false });
      });
      [...picks].sort((a, b) => b.price - a.price).forEach((u, x) => { if (x % 2 === 1) u.free = true; }); // the pair rule, as in pricing()
      const slots = this.$$('[data-tcb-slot]');
      slots.forEach((s, i) => {
        const on = i < picks.length; const was = s.classList.contains('is-on');
        s.classList.toggle('is-on', on);
        s.classList.toggle('is-new', on && !was && this.slotsPainted === true);
        const u = on ? picks[i] : null; const dot = s.querySelector('[data-tcb-slot-dot]'); const tag = s.querySelector('[data-tcb-slot-tag]');
        const hit = u && this.byVariant.get(u.variant); const v = hit && hit.product.v[hit.finish];
        dot.style.backgroundImage = v && v.img ? `url("${v.img}${v.img.includes('?') ? '&' : '?'}width=480")` : '';
        s.classList.toggle('is-free', !!(u && u.free));
        if (tag) tag.hidden = u ? !u.free : s.dataset.even !== '1';
      });
      this.slotsPainted = true;
      // their Summary control on steps 2–3: the total you pay, the list price struck through when the offer bites
      if (this.el.summary) {
        const pr = this.pricing();
        // theirs: Summary exists only once the cart holds something, on any step; an empty cart shows Previous | Next
        this.el.summary.hidden = this.cart.item_count === 0;
        this.el.total.textContent = this.money(pr.total);
        this.el.list.textContent = this.money(pr.list);
        this.el.list.hidden = pr.saved === 0;
        if (this.el.summaryInline) { this.el.summaryInline.hidden = this.el.summary.hidden; this.el.totalInline.textContent = this.el.total.textContent; this.el.listInline.textContent = this.el.list.textContent; this.el.listInline.hidden = this.el.list.hidden; }
        if (this.el.sumModal && this.el.sumModal.open) this.paintSummary();
      }
      // one button, two jobs: Next on steps 1–2 (step 2 needs a charm), checkout on step 3 (needs anything in the cart)
      if (this.step === 'charms') {
        this.el.barBtn.disabled = false;
        this.el.lock.hidden = true;
        this.el.barBtnLabel.textContent = this.labels.next;
      } else {
        this.el.barBtn.disabled = this.cart.item_count === 0;
        this.el.lock.hidden = true; // no lock on any checkout button on this page (Jorrit 2026-09-17)
        this.el.barBtnLabel.textContent = this.labels.checkout.replace('{total}', this.money(this.pricing().total));
      }
      // the panel's inline Next mirrors the bar button (label + disabled) on every repaint
      if (this.el.nextInline) { this.el.nextInline.disabled = this.el.barBtn.disabled; this.el.nextInlineLabel.textContent = this.el.barBtnLabel.textContent; }
    }

    // The offer, computed on the page from the cart (the discount Function does the real thing at checkout):
    // Buy 1 Get 1 on charms = the cheapest of each pair free, and the bracelet free once the charms reach freeAt.
    // Returns list/total/saved in cents plus how many units of each line are free ({lineKey: n}).
    pricing() {
      const bid = this.catalog.bracelet.id;
      const units = [];
      const free = {};
      this.cart.items.forEach((it) => {
        if (!this.byVariant.has(it.variant_id) || it.product_id === bid) return;
        for (let i = 0; i < it.quantity; i++) units.push({ key: it.key, price: it.original_price != null ? it.original_price : it.price });
      });
      units.sort((a, b) => b.price - a.price); // pairs form from the top; the second of each pair (the cheaper) is free
      let list = 0; let saved = 0;
      units.forEach((u, i) => { list += u.price; if (i % 2 === 1) { saved += u.price; free[u.key] = (free[u.key] || 0) + 1; } });
      const braceletFree = units.length >= this.freeAt;
      // ONE bracelet unit is free, the dearer finish first — the same rule as the discount Function
      // (apps/faye-rae-discounts/extensions/travel-charms/src/run.ts in the ops repo; change them together)
      const bracelets = this.cart.items.filter((it) => it.product_id === bid)
        .map((it) => ({ it, unit: it.original_price != null ? it.original_price : it.price }));
      bracelets.forEach((b) => { list += b.unit * b.it.quantity; });
      if (braceletFree && bracelets.length) {
        const top = bracelets.slice().sort((a, b) => b.unit - a.unit)[0];
        saved += top.unit; free[top.it.key] = 1;
      }
      return { list, saved, total: list - saved, free, charms: units.length, braceletFree };
    }

    // the Summary sheet: charm lines (FREE where the pair rule bites), the bracelet line, subtotal + you save
    paintSummary() {
      if (!this.catalog || !this.el.sumModal) return;
      const bid = this.catalog.bracelet.id;
      const pr = this.pricing();
      const row = (it) => {
        const el = this.sumTpl.content.firstElementChild.cloneNode(true);
        const hit = this.byVariant.get(it.variant_id);
        const unit = it.original_price != null ? it.original_price : it.price;
        const nFree = pr.free[it.key] || 0;
        el.dataset.lineKey = it.key;
        el.querySelector('img').src = sized(`${it.image}`, 96); // KEEP the ?v= version (2026-09-19): stripping it left browsers on the pre-replacement image (the white silver bracelet, replaced 09-16)
        el.querySelector('[data-tcb-sum-title]').textContent = (hit && hit.product.place) || it.product_title;
        el.querySelector('[data-tcb-sum-sub]').textContent = it.variant_title; // finish only: the line price sits on the right (Jorrit 2026-09-17)
        const tag = el.querySelector('[data-tcb-sum-free]');
        tag.hidden = nFree === 0;
        if (nFree > 0 && nFree < it.quantity) tag.textContent = `${nFree} ${tag.textContent.trim()}`;
        el.querySelector('[data-tcb-sum-qty]').textContent = `×${it.quantity}`;
        el.querySelector('[data-tcb-sum-price]').textContent = this.money(unit * it.quantity);
        el.classList.toggle('is-free', nFree > 0 && nFree === it.quantity);
        return el;
      };
      const charms = this.cart.items.filter((it) => this.byVariant.has(it.variant_id) && it.product_id !== bid);
      const bracelet = this.cart.items.filter((it) => it.product_id === bid);
      this.el.sumLines.replaceChildren(...charms.map(row));
      this.el.sumEmpty.hidden = charms.length > 0;
      this.el.sumCount.textContent = charms.length ? `(${pr.charms})` : '';
      const charmsList = charms.reduce((n, it) => n + (it.original_price != null ? it.original_price : it.price) * it.quantity, 0);
      const charmsFree = charms.reduce((n, it) => n + (pr.free[it.key] || 0) * (it.original_price != null ? it.original_price : it.price), 0);
      this.el.sumCharmsTotal.textContent = charms.length ? this.money(charmsList - charmsFree) : '';
      this.el.sumBracelet.replaceChildren(...bracelet.map(row));
      this.el.sumNoBracelet.hidden = bracelet.length > 0;
      // the group buttons: "Add …" (dark) while the group is empty, "Edit …" / "Change …" (quiet) once it holds something
      this.el.sumModal.querySelectorAll('.tcb__sum-goto').forEach((btn) => {
        const filled = btn.dataset.tcbSumGoto === 'bracelet' ? bracelet.length > 0 : charms.length > 0;
        btn.textContent = filled ? btn.dataset.labelEdit : btn.dataset.labelAdd;
        btn.classList.toggle('is-add', !filled);
        // the empty bracelet group carries the step's claim button: same labels, same green, the price behind the drawn dot
        if (btn.dataset.tcbSumGoto === 'bracelet' && filled) {
          const sw = this.el.brTile.querySelector('[data-tcb-br-switch]'); const to = this.braceletInCart() === 'g' ? 's' : 'g';
          btn.hidden = !this.catalog.bracelet.v[to]; // a bracelet that comes in one finish has nothing to switch to
          if (sw) btn.textContent = (to === 'g' ? sw.dataset.labelG : sw.dataset.labelS) || btn.dataset.labelEdit;
        } else if (btn.dataset.tcbSumGoto === 'bracelet') btn.hidden = false;
        const claim = btn.dataset.tcbSumGoto === 'bracelet' && !filled;
        btn.classList.toggle('is-claim', claim);
        if (claim) {
          const src = this.el.brTile.querySelector('[data-tcb-add-bracelet]'); const free = this.charmCount() >= this.freeAt;
          const label = document.createElement('span'); label.textContent = src ? (free ? (src.dataset.labelFree || src.dataset.label) : src.dataset.label) : btn.dataset.labelAdd;
          btn.replaceChildren(label);
          if (!free) { const pr2 = document.createElement('span'); pr2.className = 'tcb__add-price'; pr2.textContent = this.money(this.price.bracelet[this.finishFor(this.catalog.bracelet)]); btn.appendChild(pr2); }
        }
      });
      const brList = bracelet.reduce((n, it) => n + (it.original_price != null ? it.original_price : it.price) * it.quantity, 0);
      this.el.sumBraceletTotal.textContent = bracelet.length ? this.money(pr.braceletFree ? 0 : brList) : '';
      this.el.sumTotal.textContent = this.money(pr.total);
      this.el.sumList.textContent = this.money(pr.list);
      this.el.sumList.hidden = pr.saved === 0;
      this.el.sumSaveRow.hidden = pr.saved === 0;
      this.el.sumSave.textContent = this.money(pr.saved);
      if (this.el.sumCheckout) this.el.sumCheckout.disabled = this.cart.item_count === 0;
    }

    paintReview() {
      const rows = this.cart.items.filter((it) => this.byVariant.has(it.variant_id) && it.product_id !== this.catalog.bracelet.id);
      if (!rows.length) {
        const p = document.createElement('p'); p.className = 'tcb__review-empty';
        p.textContent = String(this.labels.first || '').replace('{left}', String(this.freeAt)).replace('{n}', '0').replace('{value}', this.money(this.price.bracelet[this.finishFor(this.catalog.bracelet)]));
        this.el.review.replaceChildren(p); return;
      }
      this.el.review.replaceChildren(...rows.map((it) => {
        const el = this.rowTpl.content.firstElementChild.cloneNode(true);
        el.dataset.lineKey = it.key;
        el.querySelector('img').src = sized(`${it.image}`, 112); // keeps ?v=, see the cart sheet's row above
        el.querySelector('[data-tcb-row-title]').textContent = (this.byVariant.get(it.variant_id).product.place) || it.product_title;
        el.querySelector('[data-tcb-row-sub]').textContent = it.variant_title;
        el.querySelector('[data-tcb-row-qty]').textContent = it.quantity;
        return el;
      }));
    }

    /* The offer pills' explanation (2026-09-20). Shoppers tapped the two offer pills and the charm step's subline as if they
       were buttons; a tap on an offer label is a question, so it opens ONE bubble with the answer. The bubble is a single element the script parks under whatever was tapped
       (it holds no scroller, so re-inserting it is safe). It closes on a second tap, a tap anywhere else (the bubble included), Escape or a
       step change; no timer, she is reading. */
    wireTips() {
      const tip = this.$('[data-tcb-tip-bubble]'); if (!tip) return;
      const sub = this.$('[data-tcb-step-sub]');
      if (sub && this.dataset.tipPair) sub.classList.add('has-tip');
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-tcb-tip-bubble]')) { this.closeTip(); return; } // it can cover the second pill row or the finish switch: a tap on it dismisses it
        const pill = e.target.closest('[data-tcb-tip]');
        if (pill) { const pair = pill.dataset.tcbTip !== 'bracelet'; this.toggleTip(pill, pair ? this.dataset.tipPair : this.dataset.tipBracelet, false, pair); return; }
        // the subline states the offer on the charm step only (on the bracelet step it is the bracelet's own line)
        if (sub && e.target.closest('[data-tcb-step-sub]') && this.step === 'charms') this.toggleTip(sub, this.dataset.tipPair, true, true);
      });
      document.addEventListener('click', (e) => { if (!tip.hidden && !(e.target.closest && e.target.closest('[data-tcb-tip], [data-tcb-tip-bubble], [data-tcb-step-sub]'))) this.closeTip(); });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.closeTip(); });
      // its top is measured when it opens: a WIDTH change (rotation, a desktop window crossing the 1000px layout) moves the pills
      // under it, so it closes. Width only: in-app browsers fire resize on every scroll as their toolbar collapses.
      let tipW = window.innerWidth;
      window.addEventListener('resize', () => { if (window.innerWidth !== tipW) { tipW = window.innerWidth; this.closeTip(); } });
    }

    toggleTip(anchor, text, plain, pair) {
      const tip = this.$('[data-tcb-tip-bubble]'); if (!tip || !text) return;
      if (!tip.hidden && this.tipAnchor === anchor) { this.closeTip(); return; }
      this.closeTip();
      const host = anchor.closest('.tcb__head, .tcb__stephead'); if (!host) return;
      this.$('[data-tcb-tip-text]').textContent = text;
      // the pair offer's example rows + closing line; {pay:N} = N charms in the SELECTED finish (before the catalog lands
      // there is no price yet: the bracketed part drops out and the row still reads)
      const rows = this.$('[data-tcb-tip-rows]'); const foot = this.$('[data-tcb-tip-foot]');
      const unit = this.price && this.price.charm ? this.price.charm[this.finish] : 0;
      const pay = (l) => (unit ? l.replace(/\{pay:(\d+)\}/g, (m, n) => this.money(unit * Number(n))) : l.replace(/\s*\(\{pay:\d+\}\)/g, '').replace(/\{pay:\d+\}/g, ''));
      const lines = pair ? (this.dataset.tipPairRows || '').split('\n').map((l) => l.trim()).filter(Boolean) : [];
      if (rows) {
        rows.textContent = '';
        lines.forEach((l) => {
          const txt = pay(l);
          const li = document.createElement('li'); const cut = txt.lastIndexOf(')') + 1; // the tail after the price wraps as one piece
          li.textContent = cut > 0 && cut < txt.length ? txt.slice(0, cut) : txt;
          if (cut > 0 && cut < txt.length) { const tail = document.createElement('span'); tail.textContent = txt.slice(cut).trim(); li.append(' ', tail); }
          rows.appendChild(li);
        });
        rows.hidden = !lines.length;
      }
      if (foot) {
        // paragraphs split on blank lines; **text** = the offer's name in bold green (built as nodes, never as HTML)
        const paras = pay(pair ? (this.dataset.tipPairFoot || '') : (this.dataset.tipBraceletFoot || '')).split(/\n\s*\n|\n/).map((t) => t.trim()).filter(Boolean);
        foot.textContent = '';
        paras.forEach((t) => { const para = document.createElement('p'); t.split('**').forEach((bit, i) => { if (!bit) return; if (i % 2) { const b = document.createElement('strong'); b.textContent = bit; para.appendChild(b); } else para.appendChild(document.createTextNode(bit)); }); foot.appendChild(para); });
        foot.hidden = !paras.length;
      }
      host.appendChild(tip);
      tip.hidden = false;
      const h = host.getBoundingClientRect(); const a = anchor.getBoundingClientRect();
      // directly under the tapped label with the caret on it (Jorrit 2026-09-20: under the whole pill ROW it sat up to two rows
      // away at 360px and read as disconnected). It may cover the pills below while open: a tap on it dismisses it.
      // a pill's bubble starts where the NEXT pill row starts (measured: the rows are not an even 6px apart, and any fixed gap
      // left a sliver of the pill below showing above the bubble); on the last row, and under the subline, a plain gap
      let gap = plain ? 9 : 6;
      if (!plain) { const below = Array.from(anchor.parentElement.children).map((el) => el.getBoundingClientRect()).filter((r) => r.height && r.top > a.bottom - 2).map((r) => r.top - a.bottom); if (below.length) gap = Math.max(3, Math.min(6, Math.floor(Math.min(...below)) - 1)); } // -1 and the floor below: pill heights are fractional, a rounded top still left 1px of the next pill
      tip.style.top = `${Math.floor(a.bottom - h.top + gap)}px`;
      // wider than a phone the bubble (max 420px) is narrower than its column: centre it under the tapped pill, clamped to the
      // column's edges (it used to start at the column's left, so at 768px the second pill sat over its far right corner). The
      // subline's bubble keeps the column's left: its anchor is a full-width line of text, not a pill.
      tip.style.left = ''; tip.style.right = ''; tip.style.width = '';
      const w = tip.offsetWidth; let left = 0;
      if (!plain && w < h.width - 1) { left = Math.round(Math.max(0, Math.min(a.left - h.left + a.width / 2 - w / 2, h.width - w))); tip.style.left = `${left}px`; tip.style.right = 'auto'; tip.style.width = `${w}px`; }
      const x = a.left - h.left - left + (plain ? Math.min(a.width / 2, 60) : a.width / 2); // a pill's caret at its centre; the long subline's near its start
      tip.style.setProperty('--tcb-tip-x', `${Math.round(Math.max(18, Math.min(x, w - 18)))}px`);
      this.tipAnchor = anchor;
      if (anchor.hasAttribute('aria-expanded')) anchor.setAttribute('aria-expanded', 'true');
      const r = tip.getBoundingClientRect(); if (r.bottom > window.innerHeight) window.scrollBy({ top: r.bottom - window.innerHeight + 12, behavior: 'smooth' });
    }

    closeTip() {
      const tip = this.$('[data-tcb-tip-bubble]'); if (!tip || tip.hidden) return;
      tip.hidden = true;
      if (this.tipAnchor && this.tipAnchor.hasAttribute('aria-expanded')) this.tipAnchor.setAttribute('aria-expanded', 'false');
      this.tipAnchor = null;
    }

    // the "select a charm" bubble above the buttons: shows for a few seconds, or until the next add / step change
    notice() {
      // both copies: the bar's bubble and the panel's inline twin (on phones the bar is off screen while the panel's Next shows)
      const ns = [this.el.notice, this.$('[data-tcb-notice-inline]')].filter(Boolean); if (!ns.length) return;
      ns.forEach((n) => { if (n.dataset.orig) n.textContent = n.dataset.orig; }); // never the error wording (see fail)
      ns.forEach((n) => { n.hidden = false; });
      const inline = ns[ns.length - 1];
      if (inline.offsetParent) { const r = inline.getBoundingClientRect(); if (r.top < 0 || r.bottom > window.innerHeight) inline.scrollIntoView({ block: 'center', behavior: 'smooth' }); }
      clearTimeout(this.noticeTimer);
      this.noticeTimer = setTimeout(() => { ns.forEach((n) => { n.hidden = true; }); }, 3500);
    }

    /* ---------------------------------------------------------------- utils */
    money(cents) {
      const amount = (cents || 0) / 100;
      let s;
      try { s = new Intl.NumberFormat(this.locale, { style: 'currency', currency: this.currency, currencyDisplay: 'narrowSymbol' }).format(amount); }
      catch (e) { s = `${this.currency} ${amount.toFixed(2)}`; }
      s = s.replace(/([.,]00)(?!\d)/, '');
      if (['CAD', 'AUD', 'NZD'].includes(this.currency) && !s.includes(this.currency)) s += ` ${this.currency}`;
      return s;
    }
    escape(str) { return String(str || '').replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch])); }
    // the error line lives in the fixed bar, which is OFF SCREEN on phones while the panel's buttons show: the panel's
    // overlay bubble carries it too, or a failed checkout read is as silent as the old "select a charm" notice was
    fail() {
      this.el.error.hidden = false;
      const n = this.$('[data-tcb-notice-inline]');
      if (n && n.offsetParent !== null || (n && window.matchMedia && window.matchMedia('(max-width: 999px)').matches)) {
        if (!n.dataset.orig) n.dataset.orig = n.textContent; // its own "select a charm" wording, put back afterwards
        n.textContent = this.el.error.textContent; n.hidden = false;
        clearTimeout(this.failTimer); this.failTimer = setTimeout(() => { n.hidden = true; n.textContent = n.dataset.orig; }, 4000);
      }
    }
  }

  customElements.define('travel-charm-builder', TravelCharmBuilder);
})();
